import { HORIZON } from '@core/core/constants'
import {
  addUploadFiles,
  selectActiveProject,
  selectActiveProjectName,
  uploadHorizonFiles,
  useAppDispatch,
  useAppSelector,
} from '@core/core/store'
import type { DataToSendEvent } from '@core/core/types'
import { getImportDataAnalyticParams } from '@core/core/utils'
import { unwrapResult } from '@reduxjs/toolkit'
import type { FC } from 'react'
import { useEffect, useState } from 'react'

import { AddingFilesModal } from './AddingFilesModal'
import type {
  AddingFilesModalContainerPropsType as PropsType,
  FilesUploadType,
  FormDataType,
} from './types'
import { AddingFilesModes } from './types'
import { useAddingFiles } from './useAddingFiles'
import { useGetDomainItems } from './useGetDomainItems'
import { useGetSurveyItems } from './useGetSurveyItems'

const AddingFilesHorizonModalContainer: FC<PropsType> = ({ files = null, treeObject, mode }) => {
  const dispatch = useAppDispatch()
  const domainItems = useGetDomainItems()
  const activeProject = useAppSelector(selectActiveProject)
  const activeProjectName = useAppSelector(selectActiveProjectName)
  const { survey2DItems, survey3DItems } = useGetSurveyItems(activeProject.id)
  const [mainFileName, setMainFileName] = useState<string>()
  const [filesUpload, setFilesUpload] = useState<FilesUploadType[]>([])

  const data: DataToSendEvent = {
    id: 'import from file_seismic_end',
    params: getImportDataAnalyticParams(activeProjectName, 'Horizon'),
  }

  const { activeFileReady, visibleModal, isOneFileDownloaded, onSave, onSaveForAll, onCancel } =
    useAddingFiles(
      files,
      data,
      (file: File, formData?: FormDataType) => {
        if (formData) {
          setFilesUpload(prevValue => [...prevValue, { file, formData }])
          dispatch(addUploadFiles([file.name]))
        }
      },
      undefined,
      setMainFileName,
    )

  useEffect(() => {
    const localDispatch = (fileUpload: FilesUploadType) => {
      const formData = fileUpload.formData
      return dispatch(
        uploadHorizonFiles({
          projectId: activeProject.id,
          data: {
            file: { data: fileUpload.file, fileName: fileUpload.file.name },
            name: formData.objectName,
            domainType: formData.domain?.id,
            survey2DUid: formData.survey2D?.id,
            cubeUid: formData.survey3D?.id,
          },
        }),
      )
    }

    const horizonMainDispatch = async () => {
      switch (mode) {
        case AddingFilesModes.Nx1:
          return horizonMainDispatch_1xN()
        case AddingFilesModes.NxN: {
          horizonMainDispatch_NxN()
          return
        }
        case undefined:
          break
      }
    }

    const horizonMainDispatch_1xN = async () => {
      const fileUpload = filesUpload.shift()

      if (fileUpload) {
        const result = await localDispatch(fileUpload)
        unwrapResult(result)
      }
    }

    const horizonMainDispatch_NxN = () => {
      for (const fileUpload of filesUpload) {
        localDispatch(fileUpload)
      }
    }

    const effectAction = async () => {
      if (filesUpload.length && !activeFileReady) {
        await horizonMainDispatch()

        setFilesUpload([])
      }
    }

    effectAction()
  }, [activeFileReady])

  const objectName = treeObject?.name ?? mainFileName

  return (
    <AddingFilesModal
      isModalOpen={visibleModal}
      isOneFileDownload={isOneFileDownloaded}
      isViewDomains={mode === AddingFilesModes.NxN || !objectName}
      objectName={mode === AddingFilesModes.NxN ? '' : objectName}
      fileName={activeFileReady?.name ?? ''}
      survey2DItems={survey2DItems}
      survey3DItems={survey3DItems}
      domainItems={domainItems}
      uploadingType={HORIZON}
      onCancel={onCancel}
      onSave={onSave}
      onSaveForAll={onSaveForAll}
    />
  )
}

export { AddingFilesHorizonModalContainer }
